import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import {
  BarChart,
  BarChartOrientation,
  BarChartTheme,
  BarColorVariant,
} from '@delmonicos/shared-design-system-react/src/components/bar-chart/bar-chart';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { TransactionReportsStatisticsByLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction-report/transaction-reports-statistics-by-location.model';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export enum ByLocationCardVariantEnum {
  Transactions = 'transactions',
  Revenue = 'revenue',
  Energy = 'energy',
}

interface ByLocationCardProps {
  data?: TransactionReportsStatisticsByLocationModel;
  variant?: ByLocationCardVariantEnum;
}

interface ByLocationVariation {
  name: string;
  theme: BarChartTheme;
  titleVariant: AccentVariantEnum;
}

const byLocationVariations: Record<
  ByLocationCardVariantEnum,
  ByLocationVariation
> = {
  [ByLocationCardVariantEnum.Transactions]: {
    name: 'transactions',
    theme: BarChartTheme.PRIMARY,
    titleVariant: AccentVariantEnum.Primary,
  },
  [ByLocationCardVariantEnum.Revenue]: {
    name: 'revenue',
    theme: BarChartTheme.DECORATIVE,
    titleVariant: AccentVariantEnum.Accent1,
  },
  [ByLocationCardVariantEnum.Energy]: {
    name: 'energy',
    theme: BarChartTheme.SECONDARY,
    titleVariant: AccentVariantEnum.Secondary,
  },
};

export const ByLocationCard = ({
  data,
  variant = ByLocationCardVariantEnum.Transactions,
}: ByLocationCardProps): React.JSX.Element => {
  const { t } = useTranslation();
  const variation = byLocationVariations[variant];

  const series = useMemo(() => {
    if (!data?.value?.length) return [];

    const sorted = [...data.value]
      .map((d, i) => ({ i, v: d.total ?? 0 }))
      .sort((a, b) => b.v - a.v);

    const topIndices = sorted.slice(0, 3).map((x) => x.i);
    const MAX_ITEMS = 10;

    let result = data.value.map((d, i) => {
      let colorVariant: BarColorVariant = BarColorVariant.BASIC;
      if (i === topIndices[0]) colorVariant = BarColorVariant.TOP1;
      else if (i === topIndices[1] || i === topIndices[2]) {
        colorVariant = BarColorVariant.HIGHLIGHT;
      }

      return {
        name: d.locationName,
        value: d.total ? Number(d.total.toFixed(2)) : undefined,
        colorVariant,
      };
    });

    if (result.length > MAX_ITEMS) {
      const top10 = result.slice(0, MAX_ITEMS);
      const others = result.slice(MAX_ITEMS);
      const othersTotal = Number(
        others.reduce((sum, item) => sum + (item.value ?? 0), 0).toFixed(2),
      );

      result = [
        ...top10,
        {
          name: t('$.statistics.cards.by-location.others'),
          value: othersTotal,
          colorVariant: BarColorVariant.BASIC,
        },
      ];
    }

    return result;
  }, [data, t]);

  return (
    <Card gap={GapEnum.M} flex={1}>
      <Stack justifyContent="space-between" alignItems="center">
        <AccentTitle
          icon="location"
          text={t('$.statistics.cards.by-location.title')}
          variant={variation.titleVariant}
        />
        <Text fontSize={TextFontSize.Large}>
          {t(`$.statistics.cards.by-location.${variant}-legend`)}
        </Text>
      </Stack>

      <BarChart
        series={series}
        theme={variation.theme}
        orientation={BarChartOrientation.HORIZONTAL}
        isLogarithmic
        showValue
      />
    </Card>
  );
};
